import type { Auth0UserInfo } from './utils';
import { FFH_ROLES, FFH_SUBSCRIPTION } from '@lib/constants';
import type { MembershipEntitlements } from '@api-service/entitlements';

/**
 * Restates a user object's membership claims from ffh-backend's answer, so the
 * role helpers that read `user[FFH_ROLES]` and `user[FFH_SUBSCRIPTION]` keep
 * working unchanged while sourcing live values (FFH-3019).
 *
 * The claims in an Auth0 session were only ever as fresh as the login that
 * minted them, and an ADR-005 session lasts months, so left alone they would
 * paywall a member who upgraded and keep serving one who lapsed.
 *
 * A degraded answer is ignored rather than applied. `isDegraded` means a lookup
 * failed and the tier fell back to FREE, which would take paid content away
 * from a paying member for as long as the subscriptions service was unwell.
 * The claims the session already carries are the better guess in that case.
 *
 * The same goes for an answer that has not arrived yet, or never does: the
 * session claims stand rather than the user reading as having no membership at
 * all. That is a deliberate trade. Treating the gap as "no entitlement" would
 * flash the paywall at every paying member on every page load, and what is
 * gated on it here is what the interface offers, not what the member is
 * allowed to have: ffh-backend decides that from the access token, which it
 * mints hourly from this same answer. useEntitlements retries and revalidates
 * so the gap closes on its own.
 */
export const applyEntitlementsToUser = <
  T extends Auth0UserInfo | null | undefined
>(
  user: T,
  entitlements: MembershipEntitlements | undefined
): T => {
  if (!user || !entitlements || entitlements.isDegraded) return user;

  return {
    ...user,
    [FFH_ROLES]: entitlements.roles,
    // The claim is absent, not null, when there is no subscription: every
    // reader tests it for truthiness, but `getUserSubscriptionInfo` promises
    // `undefined` and its callers type against that.
    [FFH_SUBSCRIPTION]: entitlements.subscription ?? undefined
  };
};
